import { APIPaths } from '../common/constants';
import interceptor from '../common/interceptor';
import { ApiResponse } from '@/apis/types';

export interface AirportContactResponse {
  data: {
    data: {
      is_success: boolean;
      message?: string;
    };
  };
}
interface modelProps {
  airport?: string;
  airline?: string;
  page?: number | string;
  search?: string;
}
//get airport contacts
export const GetAirportsContacts = (
  model: modelProps,
  token: string
): Promise<unknown> => {
  const params = new URLSearchParams();

  if (model.page !== undefined) {
    params.append('page', String(model.page));
  }

  if (model.search) {
    params.append('search', model.search);
  }

  const query = `?airport=${model.airport ?? ''}&airline=${model.airline ?? ''}`;

  const url = `${APIPaths.airport_contacts}${query}&${params.toString()}`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const AddAirportContact = (
  model: unknown,
  token: string
): Promise<AirportContactResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.airport_contacts, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//get airport contacts
export const DeleteAirportContact = (
  id: string,
  token: string
): Promise<AirportContactResponse> => {
  const url = `${APIPaths.airport_contacts}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

interface GeneralStationInfo {
  locationType?: string;
  locationAddress?: string;
  drivingDirection?: string | null;
}

interface FacilityInfoItem {
  attributes?: string;
  indicator?: string;
  additionalInfo?: string;
}

interface HolidayInfo {
  holiday?: string;
  hours?: string;
}

interface DropOffTimeInfo {
  domMinDropOffTime?: string;
  intMinDropOffTime?: string;
  domPrescreenedTime?: string;
  intPrescreenedTime?: string;
}

/**
 * Airport facility data returned by GetAirportsData API.
 * This structure is returned as an array from the API endpoint: {airportCode}/airlines/?type={airlineType}
 */
export interface AirportFacilityData {
  latitude?: string;
  longitude?: string;
  airportCode?: string;
  airportName?: string;
  drivingDirection?: string | null;
  zipCode?: string | null;
  weatherScript?: string;
  generalStationList?: GeneralStationInfo[];
  portCode?: string;
  firmsCode?: string;
  hourOfOperation?: Record<string, Record<string, string[]>>;
  facilityInfo?: FacilityInfoItem[];
  dropOffMap?: Record<string, DropOffTimeInfo>;
  holidayMap?: Record<string, HolidayInfo[]>;
}

type GetAirportsDataResponse = ApiResponse<AirportFacilityData[]>;

//get airport data [mahtab]
export const GetAirportsData = (
  query: string,
  token: string
): Promise<GetAirportsDataResponse> => {
  // LAX/airlines/?type=2
  const url = `${APIPaths.airport_data}${query}`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response getting airport data', error?.response);
        reject(error?.response);
      });
  });
};
// same as GetAirportsData but new end point for Lufthansa and airfrance
export const GetAirportsDetails = (
  query: string,
  token: string
): Promise<GetAirportsDataResponse> => {
  const url = `${APIPaths.airport_details}${query}`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response getting airport data', error?.response);
        reject(error?.response);
      });
  });
};
interface OfficeHour {
  day?: string;
  open_time?: string;
  close_time?: string;
}

interface Contact {
  contact_type?: string;
  contact_value?: string;
  contact_title?: string;
}

export interface CustomsOfficeData {
  port_code?: string;
  location_address?: string;
  source_url?: string;
  office_hours?: OfficeHour[];
  contacts?: Contact[];
}

interface GetCustomsOfficeByPortCodeResponse {
  data?: CustomsOfficeData | CustomsOfficeData[] | null;
}

export const getCustomsOfficeByPortCode = async (
  portCode: string,
  token: string
): Promise<GetCustomsOfficeByPortCodeResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.customs_office_port_code}${portCode}/`,
        token,
        null
      )
      .then((res) => {
        if (res.data?.status === 'failed' || !res.data?.data) {
          console.log(
            'No customs office data available for portCode:',
            portCode
          );
          resolve({ data: null });
        } else {
          resolve(res.data.data);
        }
      })
      .catch((error) => {
        console.log('error?.response getting customs office', error?.response);
        reject(error?.response);
      });
  });
};

export interface AllAirportData {
  message: string;
  status: number;
  is_success: boolean;
  messageTypeId: string;
  data: AirportData[];
}

export interface AllAirports {
  message: string;
  status: number;
  is_success: boolean;
  messageTypeId: string;
  page_count: number;
  total_count: number;
  data: Airport[];
}

export interface Airport {
  id: string;
  name: string;
  country_code: string;
  region_name: string;
  iata_code: string;
  icao_code: string;
  latitude: number;
  longitude: number;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface AirportData {
  country_code: string;
  region_name: string;
  iata: string;
  icao: string | null;
  airport: string;
  latitude: number;
  longitude: number;
}

export const getAllAirportData = async (
  token: string,
  queryParams: string
): Promise<AllAirportData> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.all_airport_data + queryParams, token, null)
      .then((res) => {
        resolve(res.data.data);
      })
      .catch((error) => {
        console.log(
          'error?.response getting all airport data',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const getAllAirports = async (
  search: string,
  page: number
): Promise<AllAirports> => {
  let url = `${APIPaths.airport_list}?page=${page}`;
  if (search) {
    url = `${APIPaths.airport_list}?search=${search}&page=${page}`;
  }
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, null, null)
      .then((res) => {
        resolve(res.data.data);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};
export const getAllAirlines = async (): Promise<AllAirports> => {
  const url = `${APIPaths.airport_list}airline/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, null, null)
      .then((res) => {
        resolve(res.data.data);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export interface AllAirportDelayStats {
  [airportCode: string]: AirportDelayStats;
}

export interface AirportDelayStats {
  airport_delay_counts: AirportDelayCounts;
  delayed_cancelled_pct: number;
}

export interface AirportDelayCounts {
  cancelled: number;
  delayed: number;
  total: number;
}

export const getAllAirportDelayStats = async (
  token: string
): Promise<AllAirportDelayStats> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.airport_delay, token, null)
      .then((res) => {
        resolve(res.data.data);
      })
      .catch((error) => {
        console.log(
          'error?.response getting all airport delay stats',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export interface AirportDelayStatHistory {
  filtered_data: FilteredAirportDelayStat[];
  percent_change_last_6_hrs: number;
  page_count: number;
}

export interface FilteredAirportDelayStat {
  pull_timestamp: string; // ISO 8601 datetime
  airport_delay_counts: AirportDelayCounts;
  delayed_cancelled_pct: number;
}

export const getAirportDelayStatHistory = async (
  icaoCode: string,
  token: string,
  signal?: AbortSignal
): Promise<AirportDelayStatHistory> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.airport_delay_history}?icao_code=${icaoCode}`,
        token,
        null,
        signal
      )
      .then((res) => {
        resolve(res.data.data);
      })
      .catch((error) => {
        console.log(
          'error?.response getting all airport delay stats',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export interface AirportDelayBulkHistoryResponse {
  [icaoCode: string]: AirportDelayStatHistory;
}

export const getAirportDelayStatHistoryBulk = async (
  icaoCodes: string[],
  token: string,
  signal?: AbortSignal
): Promise<AirportDelayBulkHistoryResponse> => {
  return new Promise((resolve, reject) => {
    const icaoCodesParam = icaoCodes
      .map((code) => encodeURIComponent(code))
      .join(',');
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.airport_delay_history_bulk}?icao_codes=${icaoCodesParam}`,
        token,
        null,
        signal
      )
      .then((res) => {
        resolve(res.data.data);
      })
      .catch((error) => {
        console.log(
          'error?.response getting bulk airport delay history',
          error?.response
        );
        reject(error?.response);
      });
  });
};
